import { getSeatZoneNumberForPassengerSeat, sameSeatNumber } from 'utils/seatPricing';

/**
 * Get passengers as list based on fields.
 * @param {object} fields - Fields object.
 * @returns {array} Array of passengers.
 */
export const getPassengersAsList = (fields) => {
  return Array.isArray(fields?.getAll?.()) ? fields.getAll() : [];
};

/**
 * Get way seats fallback based on passengers list and way.
 * @param {array} passengersAsList - List of passengers.
 * @param {string} way - Way of the trip.
 * @returns {array} Array of seats.
 */
export const getWaySeatsFallback = (passengersAsList = [], way) => {
  const seatFieldByWay = {
    departure: 'departureSeats',
    return: 'returnSeats',
  };
  const nestedSeatByWay = {
    departure: 'departure',
    return: 'return',
  };
  const seatField = seatFieldByWay[way];
  const nestedSeat = nestedSeatByWay[way];

  if (!seatField || !nestedSeat) return [];

  return passengersAsList
    .flatMap((passenger) => {
      const waySeats = passenger?.[seatField] ? Object.values(passenger[seatField]) : [];
      if (waySeats.length > 0) return waySeats;
      return passenger?.seats?.[nestedSeat] ? [passenger.seats[nestedSeat]] : [];
    })
    .filter(Boolean);
};

/**
 * Get effective seat price zones based on enabled flag and seat price zones.
 * @param {object} params - Parameters object.
 * @param {boolean} params.enabled - Whether seat pricing is enabled.
 * @param {array} params.seatPriceZones - Array of seat price zones.
 * @param {function} params.deriveFromSeats - Function to derive seat price zones from fallback seats.
 * @param {array} params.fallbackSeats - Array of fallback seats.
 * @returns {array} Array of effective seat price zones.
 */
export const getEffectiveSeatPriceZones = ({
  enabled,
  seatPriceZones = [],
  deriveFromSeats,
  fallbackSeats = [],
}) => {
  if (!enabled) return [];
  if (seatPriceZones.length > 0) return seatPriceZones;
  return deriveFromSeats(fallbackSeats);
};

/**
 * Find a seat in a list by comparing seat number.
 * @param {array} seats - Seats list to search.
 * @param {object} selectedSeat - Seat used as fallback/matcher.
 * @returns {object|undefined} Matching seat or selected seat fallback.
 */
const findSeatByNumber = (seats = [], selectedSeat) => {
  return seats.find((seat) => sameSeatNumber(seat?.number, selectedSeat?.number)) || selectedSeat;
};

/**
 * Build seat presentation data used by passenger cards.
 * @param {object} params - Parameters object.
 * @param {object} params.passengerFields - Passenger form fields.
 * @param {boolean} params.seatBasedPricingDeparture - Departure seat-based pricing enabled.
 * @param {boolean} params.seatBasedPricingReturn - Return seat-based pricing enabled.
 * @param {array} params.effectiveSeatPriceZonesDeparture - Effective departure seat zones.
 * @param {array} params.effectiveSeatPriceZonesReturn - Effective return seat zones.
 * @returns {object} Seat presentation info for UI rendering (seatsObject, departureSeatCountForZone, departureSeatZone, returnSeatZone, departureSeatRgbColor, returnSeatRgbColor).
 */
export const getPassengerSeatPresentation = ({
  passengerFields = {},
  seatBasedPricingDeparture = false,
  seatBasedPricingReturn = false,
  effectiveSeatPriceZonesDeparture = [],
  effectiveSeatPriceZonesReturn = [],
}) => {
  const { seats, departureSeats, returnSeats } = passengerFields;
  const departureSeatsArray = Object.values(departureSeats || {});
  const returnSeatsArray = Object.values(returnSeats || {});
  const depLegSeats = departureSeatsArray.length ? departureSeatsArray : [seats?.departure];
  const retLegSeats = returnSeatsArray.length ? returnSeatsArray : [seats?.return];
  const seatsObject = [...depLegSeats, ...retLegSeats];

  let departureSeatCountForZone = 0;
  if (departureSeatsArray.length) {
    departureSeatCountForZone = departureSeatsArray.length;
  } else if (seats?.departure) {
    departureSeatCountForZone = 1;
  }

  const departureSeatForZone = findSeatByNumber(departureSeatsArray, seats?.departure);
  const returnSeatForZone = findSeatByNumber(returnSeatsArray, seats?.return);
  const departureSeatRgbColor = departureSeatForZone?.rgbColor ?? departureSeatForZone?.rgb_color;
  const returnSeatRgbColor = returnSeatForZone?.rgbColor ?? returnSeatForZone?.rgb_color;

  const departureSeatZone =
    seatBasedPricingDeparture && departureSeatForZone
      ? getSeatZoneNumberForPassengerSeat(departureSeatForZone, effectiveSeatPriceZonesDeparture)
      : undefined;
  const returnSeatZone =
    seatBasedPricingReturn && returnSeatForZone
      ? getSeatZoneNumberForPassengerSeat(returnSeatForZone, effectiveSeatPriceZonesReturn)
      : undefined;

  return {
    seatsObject,
    departureSeatCountForZone,
    departureSeatZone,
    returnSeatZone,
    departureSeatRgbColor,
    returnSeatRgbColor,
  };
};
